import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Stack,
  Paper,
  Tooltip,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import CustomContainedButton from "../../../Components/CustomContainedButton";
import apiRequest from "../../../request/apiRequest/apiRequest";
import { useAppDispatch } from "../../../hooks/storeHooks";
import { allApis } from "../../../request/config";
import { showSnack } from "../../../store/slices/constants";
import { ObjectValues } from "../../login";
import { rowCenter, rowSB } from "../../../GlobalStyles";
import CustomProgress from "../../../Components/CustomProgress";
import CustomIconButton from "../../../Components/CustomIconButton";
import SvgPreview from "../../../Components/SvgPreview";
import { cross } from "../../../assets/svgs";

interface Note {
  id: number;
  note: string;
  created_at: string;
  created_by_name: string;
  is_pinned: boolean;
}

interface NotesModalProps {
  open: boolean;
  onClose: () => void;
  row: ObjectValues;onReFetch:any
}

const NotesModal: React.FC<NotesModalProps> = ({
  open,
  onClose,
  row,onReFetch
}) => {
  const dispatch = useAppDispatch();

  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState("");
  const [loader, setLoader] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editedText, setEditedText] = useState("");

  useEffect(() => {
    if (open) fetchNotes();
  }, [open]);

  // -----------------------------
  // 🔹 Fetch Notes
  // -----------------------------
  const fetchNotes = async () => {
    setLoader(true);
    try {
      const res: any = await apiRequest({
        api: allApis.notes.GET(row.type.toLowerCase() ?? "exam", row.id),
        method: "GET",
      });
      if (!res.error) {
        setNotes(res || []);
      } else {
        dispatch(
          showSnack({
            open: true,
            severity: "error",
            message: res.msg,
          })
        );
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoader(false);
    }
  };

  // -----------------------------
  // 🔹 Add Note
  // -----------------------------
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setLoader(true);
    setIsSubmitting(true);
    try {
      const payload = {
        parent_model: row.type.toLowerCase(),
        parent_id: row.id,
        note: newNote.trim(),
        is_pinned: notes.length === 0 ? true : false,
      };

      const res: any = await apiRequest({
        api: allApis.notes.CREATE,
        method: "POST",
        payload,
      });

      if (res.error) {
        dispatch(
          showSnack({ open: true, severity: "error", message: res.msg })
        );
      } else {
        setNewNote("");
        onReFetch(row.type)
        fetchNotes();
      }
    } catch (error) {
      console.error("Error adding note:", error);
    } finally {
      setLoader(false);
      setIsSubmitting(false);
    }
  };

  // -----------------------------
  // 🔹 Delete Note
  // -----------------------------
  const handleDelete = async (id: number) => {
    setLoader(true);
    try {
      const res: any = await apiRequest({
        api: allApis.notes.DELETE(id),
        method: "DELETE",
        payload: { id },
      });

      if (res.error) {
        dispatch(
          showSnack({ open: true, severity: "error", message: res.msg })
        );
      } else {
      
        onReFetch(row.type)
        fetchNotes();
      }
    } catch (error) {
      console.error("Error deleting note:", error);
    } finally {
      setLoader(false);
    }
  };

  // -----------------------------
  // 🔹 Pin / Unpin or Edit Note
  // -----------------------------
  const handleUpdateNote = async (updatedNote: Note) => {
    setLoader(true);
    try {
      const res: any = await apiRequest({
        api: allApis.notes.PUT(updatedNote.id),
        method: "PUT",
        payload: {
          parent_model: row.type.toLowerCase(),
          parent_id: row.id,
          note: updatedNote.note,
          is_pinned: updatedNote.is_pinned,
        },
      });

      if (res.error) {
        dispatch(
          showSnack({ open: true, severity: "error", message: res.msg })
        );
      } else {
      
        onReFetch(row.type)
        fetchNotes();
      }
    } catch (error) {
      console.error("Error updating note:", error);
    } finally {
      setLoader(false);
    }
  };

  const handlePinToggle = (note: Note) => {
    const updated = { ...note, is_pinned: !note.is_pinned };
    handleUpdateNote(updated);
  };

  // -----------------------------
  // 🔹 Edit Note (UI)
  // -----------------------------
  const handleEditClick = (note: Note) => {
    setEditingNoteId(note.id);
    setEditedText(note.note);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditedText("");
  };

  const handleSaveEdit = (note: Note) => {
    const updated = { ...note, note: editedText };
    handleUpdateNote(updated);
    setEditingNoteId(null);
    setEditedText("");
  };

  // ✅ Keep pinned note on top
  const sortedNotes = [
    ...notes.filter((n) => n.is_pinned),
    ...notes.filter((n) => !n.is_pinned),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: 600, pb: 1, m: 0, p: "20px 15px 15px", ...rowSB }}
        id="customized-dialog-title"
      >
        {`Notes for ${row.label}`}
        <CustomIconButton
          onClick={onClose}
          noHover={true}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          content={<SvgPreview svg={cross} />}
        />
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          View, edit, or pinned notes. Add a new note below.
        </Typography>

        {/* ✅ Add Note Box */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: 2,
            mb: 2,
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Enter your note here..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Stack direction="row" justifyContent="flex-end">
            <CustomContainedButton
              content={isSubmitting ? "Saving..." : "Add Note"}
              otherProps={{
                onClick: handleAddNote,
                disabled: !newNote.trim() || isSubmitting,
              }}
            />
          </Stack>
        </Paper>

        <Divider sx={{ mb: 2 }} />

        {/* ✅ Scrollable Notes List */}
        <Box
          className="scrollBluePrimarythin"
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 1,
            maxHeight: "calc(80vh - 250px)",
          }}
        >
          {loader ? (
            <Stack sx={{ ...rowCenter, height: "100%", width: "100%" }}>
              <CustomProgress />
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {loader ? (
                <Typography textAlign="center" color="text.secondary">
                  Loading notes...
                </Typography>
              ) : sortedNotes.length === 0 ? (
                <Typography textAlign="center" color="text.secondary">
                  No notes available.
                </Typography>
              ) : (
                sortedNotes.map((note) => (
                  <Paper
                    key={note.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: note.is_pinned ? "#1976d2" : "divider",
                      boxShadow: note.is_pinned
                        ? "0 0 6px rgba(25,118,210,0.3)"
                        : "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1, mr: 1 }}>
                      {editingNoteId === note.id ? (
                        <>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleSaveEdit(note)}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              color="default"
                              size="small"
                              onClick={handleCancelEdit}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </>
                      ) : (
                        <>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {note.note}
                            </Typography>
                            {note.is_pinned && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#1976d2",
                                  fontWeight: 600,
                                  border: "1px solid #1976d2",
                                  borderRadius: "6px",
                                  px: 0.8,
                                  py: 0.1,
                                }}
                              >
                                Pinned
                              </Typography>
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Sent by {note.created_by_name} • {note.created_at}
                          </Typography>
                        </>
                      )}
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={0.5}>
                      {editingNoteId !== note.id && (
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={() => handleEditClick(note)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={note.is_pinned ? "Unpin" : "Pin"}>
                        <IconButton
                          onClick={() => handlePinToggle(note)}
                          size="small"
                          color={note.is_pinned ? "primary" : "default"}
                        >
                          {note.is_pinned ? (
                            <PushPinIcon fontSize="small" />
                          ) : (
                            <PushPinOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(note.id)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NotesModal;
